import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash-es'
import keyboardKey from 'keyboard-key'
import { eventStack } from '../../../../utils'
import TextField from '../../atoms/TextField/TextField'
import { AggregateListItem, List, ListDivider, ListSubheader } from '../List/List'
import styles from './Search.module.scss'
import { OnOff, TextFieldTypes } from 'components/atoms/TextField/text-field.types'
import Label from 'views/components/atoms/Label/Label'
import TextInput from 'views/components/atoms/TextField/TextInput'
import { titleCase } from 'utils/string'
import InfoBox from 'views/components/atoms/InfoBox/InfoBox'

// ============================================================================
// Types
// ============================================================================

export interface SearchResult {
  id?: string
  text: string
  secondaryText?: string
  tertiaryText?: string
  children?: React.ReactNode
}

export type SearchResultSets = SearchResult[][]

/**
 * A labelled block of results. `heading` renders as a subheading above the block;
 * omit it for an unlabelled group (the shape a plain `SearchResultSets` normalises to).
 */
export interface SearchResultGroup {
  heading?: React.ReactNode
  results: SearchResult[]
}

export interface SearchPropTypes {
  className?: string
  name?: string
  placeholder?: string
  value?: string
  label?: string
  labelHint?: string
  required?: boolean
  disabled?: boolean
  error?: string
  minCharacters?: number
  noResultsMessage?: React.ReactNode
  renderNoResultsContent?: (msg: React.ReactNode) => React.ReactNode
  isOpen?: boolean
  isLoading?: boolean
  hasIdentification?: boolean
  resultSets?: SearchResultSets
  /** Labelled result blocks. Takes precedence over `resultSets` when non-empty. */
  resultGroups?: SearchResultGroup[]
  defaultResultSets?: SearchResultSets
  alternativeResultSets?: SearchResultSets
  showAlternativeSearchTrigger?: boolean
  /** True while the alternative (Google) search is in flight. */
  isAlternativeLoading?: boolean
  /** True once an alternative search has come back for the current query, empty or not. */
  hasSearchedAlternative?: boolean
  showExistingPropertyTags?: boolean
  showTertiaryText?: boolean
  showNoResults?: boolean
  updateValueOnSelect?: boolean
  focusOnMount?: boolean
  onBlur?: (e: any, data: any) => void
  onFocus?: (e: any, data: any) => void
  onMouseDown?: (e: any, data: any) => void
  onResultSelect?: (e: any, data: any) => void
  onSearchChange?: (e: any, data: any) => void
  onAlternativeSearchClick?: (query: string) => void
}

// ============================================================================
// Helpers & constants
// ============================================================================

const isResultSetEmpty = (resultSet: SearchResultSets = []) => !resultSet.some(rs => !isEmpty(rs))

const areGroupsEmpty = (groups: SearchResultGroup[] = []) => !groups.some(g => !isEmpty(g.results))

// Normalise a plain set-of-sets into headingless groups so both inputs share one render/nav path.
const toGroups = (sets: SearchResultSets = []): SearchResultGroup[] => sets.map(results => ({ results }))

const EXISTING_STATUS_LABEL = (
  <Label circular size="md" style={{ color: 'white', opacity: '.64', backgroundColor: '#35383b' }}>
    Existing
  </Label>
)

const TERTIARY_STRIP_RE = /([\w/\d])*\/([\w/\d])*/gi

// ============================================================================
// Component
// ============================================================================

const Search = (props: SearchPropTypes): React.ReactElement => {
  const {
    className,
    name,
    placeholder,
    value: valueProp,
    label,
    labelHint,
    required,
    disabled,
    error,
    minCharacters = 1,
    noResultsMessage = 'No results found.',
    renderNoResultsContent,
    isOpen: isOpenProp,
    isLoading,
    hasIdentification,
    resultSets = [],
    resultGroups = [],
    defaultResultSets = [],
    alternativeResultSets = [],
    showAlternativeSearchTrigger = false,
    isAlternativeLoading = false,
    hasSearchedAlternative = false,
    showExistingPropertyTags = false,
    showTertiaryText = false,
    showNoResults = true,
    updateValueOnSelect = false,
    focusOnMount = false,
    onBlur,
    onFocus,
    onMouseDown,
    onResultSelect,
    onSearchChange,
    onAlternativeSearchClick,
  } = props

  // ----------------------------------------------------------------------------
  // State and refs
  // ----------------------------------------------------------------------------

  const [isOpen, setIsOpen] = useState<boolean>(isOpenProp ?? false)
  const [isFocused, setIsFocused] = useState(false)
  const [value, setValue] = useState<string>(valueProp ?? '')
  const [selectedSet, setSelectedSet] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isDropdownOverflowingWindow, setIsDropdownOverflowingWindow] = useState(false)
  // Set the moment the alternative search is asked for, so the dropdown can say something during
  // the debounce that runs before the request is even dispatched.
  const [isAlternativeSearchPending, setIsAlternativeSearchPending] = useState(false)

  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const searchFieldRef = useRef<HTMLInputElement | null>(null)
  const isMouseDownRef = useRef(false)
  const rafRef = useRef<number | null>(null)

  // Mirror latest prop values in refs
  const propsRef = useRef(props)
  const stateRef = useRef({ isOpen, value })
  useEffect(() => {
    propsRef.current = props
  })
  useEffect(() => {
    stateRef.current = { isOpen, value }
  }, [isOpen, value])

  const setSearchFieldRef = useCallback((ref: any) => {
    searchFieldRef.current = ref
  }, [])

  // ----------------------------------------------------------------------------
  // Aria IDs
  // ----------------------------------------------------------------------------

  const reactId = useId()
  const listboxId = `${reactId}-listbox`
  const getOptionId = (groupIdx: number, itemIdx: number, isAlternative: boolean) =>
    `${reactId}-${isAlternative ? 'alt' : 'main'}-${groupIdx}-${itemIdx}`

  // ----------------------------------------------------------------------------
  // Result emptiness checks
  // ----------------------------------------------------------------------------

  const resultSetsEmpty = useMemo(() => isResultSetEmpty(resultSets), [resultSets])
  const resultGroupsEmpty = useMemo(() => areGroupsEmpty(resultGroups), [resultGroups])
  const defaultResultSetsEmpty = useMemo(() => isResultSetEmpty(defaultResultSets), [defaultResultSets])
  const alternativeResultSetsEmpty = useMemo(() => isResultSetEmpty(alternativeResultSets), [alternativeResultSets])

  // ----------------------------------------------------------------------------
  // Resolved groups (single source of truth for rendering and keyboard navigation)
  // ----------------------------------------------------------------------------

  // Precedence: explicit labelled groups > active result sets > default result sets.
  const displayedGroups = useMemo<SearchResultGroup[]>(() => {
    let groups: SearchResultGroup[] = !resultGroupsEmpty
      ? resultGroups
      : !resultSetsEmpty
      ? toGroups(resultSets)
      : toGroups(defaultResultSets)

    if (showExistingPropertyTags) {
      groups = groups.map(group => ({
        ...group,
        results: group.results.map(r => ({ ...r, children: r.id ? EXISTING_STATUS_LABEL : null })),
      }))
    }

    if (showTertiaryText) {
      groups = groups.map(group => ({
        ...group,
        results: group.results.map(r => ({
          ...r,
          tertiaryText: r.tertiaryText ? titleCase(r.tertiaryText.replaceAll(TERTIARY_STRIP_RE, '')) : undefined,
        })),
      }))
    }

    return groups
  }, [
    resultGroups,
    resultSets,
    defaultResultSets,
    resultGroupsEmpty,
    resultSetsEmpty,
    showExistingPropertyTags,
    showTertiaryText,
  ])

  const alternativeGroups = useMemo<SearchResultGroup[]>(
    () => (alternativeResultSetsEmpty ? [] : toGroups(alternativeResultSets)),
    [alternativeResultSets, alternativeResultSetsEmpty],
  )

  const hasDisplayedResults = useMemo(() => !areGroupsEmpty(displayedGroups), [displayedGroups])

  // ----------------------------------------------------------------------------
  // Alternative (Google) search state
  // ----------------------------------------------------------------------------

  // `pending` covers the gap between the click and the request; `isAlternativeLoading` covers the
  // request itself. Both clear once a result has come back for this query.
  const isSearchingAlternative = (isAlternativeSearchPending && !hasSearchedAlternative) || isAlternativeLoading
  const alternativeAvailable = showAlternativeSearchTrigger && alternativeResultSetsEmpty
  const canRunAlternativeSearch = alternativeAvailable && !isSearchingAlternative && !hasSearchedAlternative

  // ----------------------------------------------------------------------------
  // Open, close and selection handlers
  // ----------------------------------------------------------------------------

  const open = useCallback(() => setIsOpen(true), [])

  // `e` is only present when this runs as the document-level outside-click handler.
  const close = useCallback((e?: Event) => {
    // That listener is registered in the CAPTURE phase, so it runs before the click reaches the
    // item that was clicked. Closing here unmounts the dropdown mid-dispatch and React then drops
    // the click, so a click inside the dropdown never reaches its own onClick. Selections close
    // the dropdown themselves; everything else in there is meant to stay open.
    const target = e?.target as Node | null
    if (target && dropdownRef.current?.contains(target)) return

    setIsOpen(false)
    setSelectedSet(0)
    setSelectedIndex(0)
  }, [])

  const tryOpen = useCallback(
    (currentValue: string = stateRef.current.value) => {
      if ((!defaultResultSetsEmpty && resultSetsEmpty && resultGroupsEmpty) || currentValue.length >= minCharacters) {
        open()
      }
    },
    [defaultResultSetsEmpty, resultSetsEmpty, resultGroupsEmpty, minCharacters, open],
  )

  const handleResultSelect = useCallback(
    (result: SearchResult, e: any) => {
      if (onResultSelect) onResultSelect(e, { ...propsRef.current, result })
      if (updateValueOnSelect) setValue(result.text)
      close()
    },
    [onResultSelect, updateValueOnSelect, close],
  )

  const runAlternativeSearch = useCallback((query: string) => {
    setIsAlternativeSearchPending(true)
    propsRef.current.onAlternativeSearchClick?.(query)
  }, [])

  // ----------------------------------------------------------------------------
  // Keyboard navigation
  // ----------------------------------------------------------------------------

  const scrollSelectedItemIntoView = useCallback(() => {
    const dropdown = dropdownRef.current
    if (!dropdown) return
    const item = dropdown.querySelector<HTMLLIElement>("li[class*='item__highlighted']")
    item?.scrollIntoView({ block: 'nearest' })
  }, [])

  const moveSelectionBy = useCallback(
    (offset: number) => {
      const lastSet = displayedGroups.length - 1
      if (lastSet < 0) return

      const currentSetLen = displayedGroups[selectedSet]?.results.length ?? 0
      const lastIndex = currentSetLen - 1
      let nextSet = selectedSet
      let nextIndex = selectedIndex + offset

      if (nextIndex > lastIndex) {
        nextSet = selectedSet + 1
        nextIndex = 0

        if (nextSet > lastSet) nextSet = 0
      } else if (nextIndex < 0) {
        nextSet = selectedSet - 1
        nextIndex = lastIndex

        if (nextSet < 0) {
          nextSet = lastSet
          nextIndex = displayedGroups[lastSet].results.length - 1
        }
      }

      setSelectedSet(nextSet)
      setSelectedIndex(nextIndex)
    },
    [displayedGroups, selectedSet, selectedIndex],
  )

  const moveSelectionOnKeyDown = useCallback(
    (e: any) => {
      switch (keyboardKey.getCode(e)) {
        case keyboardKey.ArrowDown:
          e.preventDefault()
          moveSelectionBy(1)
          break
        case keyboardKey.ArrowUp:
          e.preventDefault()
          moveSelectionBy(-1)
          break
        default:
          break
      }
    },
    [moveSelectionBy],
  )

  const selectItemOnEnter = useCallback(
    (e: any) => {
      if (keyboardKey.getCode(e) !== keyboardKey.Enter) return

      e.preventDefault()

      const result = displayedGroups[selectedSet]?.results?.[selectedIndex]

      if (result) {
        setValue(result.text)
        handleResultSelect(result, e)
      } else if (canRunAlternativeSearch) {
        // The trigger is the only thing left in the dropdown - Enter should run it, not dead-end.
        runAlternativeSearch(stateRef.current.value)
      } else {
        close()
      }
    },
    [
      displayedGroups,
      selectedSet,
      selectedIndex,
      handleResultSelect,
      close,
      canRunAlternativeSearch,
      runAlternativeSearch,
    ],
  )

  const closeOnEscape = useCallback(
    (e: any) => {
      if (keyboardKey.getCode(e) !== keyboardKey.Escape) return
      e.preventDefault()
      close()
    },
    [close],
  )

  // ----------------------------------------------------------------------------
  // Mouse and input handlers
  // ----------------------------------------------------------------------------

  const handleDocumentMouseUp = useCallback(() => {
    isMouseDownRef.current = false
    eventStack.unsub('mouseup', handleDocumentMouseUp)
  }, [])

  const handleMouseDown = (e: any) => {
    isMouseDownRef.current = true
    if (onMouseDown) onMouseDown(e, propsRef.current)
    eventStack.sub('mouseup', handleDocumentMouseUp)
  }

  const handleSearchChange = (e: any) => {
    const query: string = e.target.value

    e.stopPropagation()

    if (onSearchChange) onSearchChange(e, { ...propsRef.current, value: query })

    // A new query means the previous alternative search no longer applies.
    setIsAlternativeSearchPending(false)

    if (query.length < minCharacters) {
      close()
    } else if (!stateRef.current.isOpen) {
      tryOpen(query)
    }
    setValue(query)
  }

  const handleBlur = (e: any) => {
    if (onBlur) onBlur(e, propsRef.current)
    setIsFocused(false)
    if (!isMouseDownRef.current) close()
  }

  const handleFocus = (e: any) => {
    if (onFocus) onFocus(e, propsRef.current)
    setIsFocused(true)
    if (!isMouseDownRef.current) tryOpen()
  }

  const handleInputClick = (e: any) => {
    e.nativeEvent.stopImmediatePropagation()
    tryOpen()
  }

  const handleItemClick = (payload: { result: SearchResult; resultsIndex: number; index: number }, e: any) => {
    e.nativeEvent.stopImmediatePropagation()
    handleResultSelect(payload.result, e)
  }

  const handleAlternativeSearchClick = (query: string) => {
    runAlternativeSearch(query)
    tryOpen()
  }

  // ----------------------------------------------------------------------------
  // Dropdown overflow handling
  // ----------------------------------------------------------------------------

  // requestAnimationFrame throttling: reduces frequency of overflow checks during scroll/resize to once per frame
  const updateDropdownOverflow = useCallback(() => {
    if (rafRef.current !== null) return

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null

      const dropdown = dropdownRef.current
      const anchor = searchFieldRef.current
      if (!dropdown || !anchor) {
        setIsDropdownOverflowingWindow(false)
        return
      }

      // Anchor the overflow test to the input's bottom edge, NOT the dropdown's own rect.
      // The `is-overflowing-window` class flips the dropdown *above* the input, which moves the
      // dropdown's rect — measuring that rect made the predicate depend on its own result and
      // oscillate (below → overflows → flip above → fits → flip below → …), the visible jitter.
      // The input's bottom and the dropdown's height are both stable across the flip, so the
      // "would it overflow if placed below" question now has one stable answer and settles.
      const { bottom } = anchor.getBoundingClientRect()
      setIsDropdownOverflowingWindow(bottom + dropdown.offsetHeight > window.innerHeight)
    })
  }, [])

  // ----------------------------------------------------------------------------
  // Effects
  // ----------------------------------------------------------------------------

  // Sync isOpen prop
  useEffect(() => {
    if (isOpenProp !== undefined && isOpenProp !== stateRef.current.isOpen) {
      if (isOpenProp) open()
      else close()
    }
  }, [isOpenProp, open, close])

  // Sync value prop
  useEffect(() => {
    if (valueProp !== undefined && valueProp !== stateRef.current.value) {
      setValue(valueProp)
    }
  }, [valueProp])

  // Focus input on mount
  useEffect(() => {
    if (!focusOnMount) return

    const timer = setTimeout(() => {
      searchFieldRef.current?.focus()
    }, 300)
    return () => clearTimeout(timer)
  }, [focusOnMount])

  // Track dropdown overflow on scroll/resize
  useEffect(() => {
    document.addEventListener('scroll', updateDropdownOverflow, { passive: true })
    window.addEventListener('resize', updateDropdownOverflow, { passive: true })
    return () => {
      document.removeEventListener('scroll', updateDropdownOverflow)
      window.removeEventListener('resize', updateDropdownOverflow)
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [updateDropdownOverflow])

  // Re-measure overflow when result sets change
  useEffect(() => {
    updateDropdownOverflow()
  }, [displayedGroups, alternativeGroups, updateDropdownOverflow])

  // Clean up any in-flight mouseup subscription
  useEffect(() => {
    return () => eventStack.unsub('mouseup', handleDocumentMouseUp)
  }, [handleDocumentMouseUp])

  // Click and keydown listeners, scoped to when the dropdown is open
  useEffect(() => {
    if (!isOpen) return

    eventStack.sub('click', close)
    eventStack.sub('keydown', [closeOnEscape, moveSelectionOnKeyDown, selectItemOnEnter])
    return () => {
      eventStack.unsub('click', close)
      eventStack.unsub('keydown', [closeOnEscape, moveSelectionOnKeyDown, selectItemOnEnter])
    }
  }, [isOpen, close, closeOnEscape, moveSelectionOnKeyDown, selectItemOnEnter])

  // Scroll the highlighted item into view after a selection change.
  useEffect(() => {
    if (!isOpen) return
    scrollSelectedItemIntoView()
  }, [isOpen, selectedSet, selectedIndex, scrollSelectedItemIntoView])

  // ----------------------------------------------------------------------------
  // Render data
  // ----------------------------------------------------------------------------

  const activeOptionId =
    isOpen && displayedGroups[selectedSet]?.results?.[selectedIndex]
      ? getOptionId(selectedSet, selectedIndex, false)
      : undefined

  // ----------------------------------------------------------------------------
  // Render helpers
  // ----------------------------------------------------------------------------

  const renderResultGroups = (groups: SearchResultGroup[], isAlternative: boolean) =>
    groups.map((group, resultsIndex) => (
      <React.Fragment key={resultsIndex}>
        {group.heading != null && group.heading !== '' && <ListSubheader>{group.heading}</ListSubheader>}
        {group.results.map((result, i) => {
          const highlighted = !isAlternative && selectedSet === resultsIndex && selectedIndex === i
          return (
            <AggregateListItem
              key={result.id ? `${result.id}-${i}` : i}
              id={getOptionId(resultsIndex, i, isAlternative)}
              role="option"
              aria-selected={highlighted || undefined}
              highlighted={highlighted}
              onClick={(e: any) => handleItemClick({ result, resultsIndex, index: i }, e)}
              text={result.text}
              secondaryText={result.secondaryText}
              tertiaryText={!isAlternative ? result.tertiaryText : undefined}
            >
              {!isAlternative ? result.children : undefined}
            </AggregateListItem>
          )
        })}
        {resultsIndex + 1 < groups.length && <ListDivider />}
      </React.Fragment>
    ))

  const renderNoResults = () => {
    if (typeof renderNoResultsContent === 'function') {
      return <div className={styles['no-results']}>{renderNoResultsContent(noResultsMessage)}</div>
    }
    return (
      <div className={styles['no-results']}>
        <List>
          <AggregateListItem text={noResultsMessage} />
        </List>
      </div>
    )
  }

  const renderResults = () => {
    const dropdownClasses = classNames(styles.dropdown, {
      [styles['is-overflowing-window']]: isDropdownOverflowingWindow,
    })

    // Nothing matched at all, but the dropdown is still rendered so the alternative-search
    // trigger below stays reachable - keep the "no results" copy alongside it.
    const showEmptyMessage = showNoResults && !hasDisplayedResults && alternativeResultSetsEmpty
    const usesCustomNoResults = typeof renderNoResultsContent === 'function'

    // A Google search that comes back with nothing leaves every result set exactly as it was, so
    // without this the dropdown is identical before and after and the search looks like a no-op.
    const alternativeStatus = !alternativeAvailable
      ? null
      : isSearchingAlternative
      ? 'Searching Google for street addresses...'
      : hasSearchedAlternative
      ? 'No street addresses found on Google.'
      : null

    return (
      <div className={dropdownClasses} ref={dropdownRef} role="listbox" id={listboxId}>
        {showEmptyMessage && usesCustomNoResults && renderNoResultsContent?.(noResultsMessage)}
        {((showEmptyMessage && !usesCustomNoResults) || alternativeStatus) && (
          // Deliberately its own non-interactive List: this is copy, not a selectable option.
          <List>
            {showEmptyMessage && !usesCustomNoResults && <AggregateListItem text={noResultsMessage} />}
            {alternativeStatus && <AggregateListItem text={alternativeStatus} />}
          </List>
        )}
        <List interactive>
          {renderResultGroups(displayedGroups, false)}
          {canRunAlternativeSearch && (
            <AggregateListItem
              className={styles['alt-search-trigger']}
              id={`${reactId}-alt-trigger`}
              role="option"
              text="Search Google for street address"
              onClick={() => handleAlternativeSearchClick(value)}
              highlighted={true}
            />
          )}
          {renderResultGroups(alternativeGroups, true)}
        </List>
      </div>
    )
  }

  const renderMenuContent = () => {
    if (resultSetsEmpty && resultGroupsEmpty && alternativeResultSetsEmpty && defaultResultSetsEmpty) {
      if (!isOpen || isLoading) return null
      // An empty search is exactly when the alternative search matters most, so render the full
      // dropdown (which carries the trigger) rather than the bare "no results" panel.
      if (showAlternativeSearchTrigger) return renderResults()
      return showNoResults ? renderNoResults() : null
    }
    return renderResults()
  }

  // ----------------------------------------------------------------------------
  // Final render
  // ----------------------------------------------------------------------------

  const classes = classNames([styles.root, className], {
    [styles.open]: isOpen,
    [styles.loading]: isLoading && isFocused,
    [styles.hasIdentification]: hasIdentification,
    [styles.showTertiaryText]: showTertiaryText,
  })

  return (
    <>
      <div className={classes} onMouseDown={handleMouseDown}>
        <TextField
          inputComponent={
            <TextInput
              type={TextFieldTypes.text}
              value={value}
              name={name}
              onChange={handleSearchChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onClick={handleInputClick}
              autoComplete={OnOff.off}
              placeholder={placeholder}
              disabled={disabled}
              ref={setSearchFieldRef}
              role="combobox"
              aria-expanded={isOpen}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={activeOptionId}
            />
          }
          label={label}
          labelHint={labelHint}
          required={required}
          disabled={disabled}
          error={error}
        />
        {renderMenuContent()}
      </div>
    </>
  )
}

export default Search
